// Ingredient identity key. HAND-SYNCED COPY of src/lib/itemIdentity.js (Edge
// Functions cannot import from src/); tests/item-identity.test.mjs compares
// the two on a large corpus and fails if they ever disagree. Change both
// together. The rationale for every rule is documented in the src/ copy and in
// docs/2026-09-20-inventory-design.md, 3.2.

const DROPPED_WORDS = new Set(["small", "medium", "large", "big", "jumbo", "organic"]);

const IRREGULAR_PLURALS: Record<string, string> = { leaves: "leaf", loaves: "loaf", halves: "half" };

// Spellings of one food that the general rules above can't unify. Deliberately
// a short, explicit list -- not fuzzy matching -- so each entry is a decision
// that two spellings are the same product (from a scan of the household's own
// ingredient names, 2026-09-27). Applied to the whole cleaned name.
// NOT here on purpose: "chile" vs "chili" (chili powder is a spice blend, chile
// powder is pure ground chile), and a bare "creme" (creme fraiche is not cream).
const SPELLING_VARIANTS: Array<[RegExp, string]> = [
  [/\bchilli\b/g, "chili"],
  [/\bchillies\b/g, "chilies"],
  [/\bcorn starch\b/g, "cornstarch"],
  [/\bbread crumbs?\b/g, "breadcrumbs"],
  [/\bchick peas?\b/g, "chickpeas"],
  [/\bsemi sweet\b/g, "semisweet"],
  [/\bmarshmallow creme\b/g, "marshmallow cream"],
];

function singularize(word: string): string {
  if (word.length < 4) return word;
  if (IRREGULAR_PLURALS[word]) return IRREGULAR_PLURALS[word];
  if (word.endsWith("ies") && word.length > 4) return word.slice(0, -3) + "y";
  if (word.endsWith("ie") && word.length >= 5) return word.slice(0, -2) + "y";
  if (/(oes|ches|shes|xes|zes|sses)$/.test(word)) return word.slice(0, -2);
  if (word.endsWith("s") && !/(ss|us|is)$/.test(word)) return word.slice(0, -1);
  return word;
}

export function normalizeIngredientName(name: unknown): string {
  if (name == null) return "";
  let s = String(name).toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

  let previous: string;
  do {
    previous = s;
    s = s.replace(/\([^()]*\)/g, " ");
  } while (s !== previous);
  s = s.replace(/\(.*$/, " ").replace(/[()]/g, " ");

  s = s.split(/[,;]/)[0];

  s = s.replace(/['‘’`]/g, "").replace(/[^\p{L}\p{N}]+/gu, " ").trim();

  s = s.replace(/\bextra large\b/g, " ");
  const kept = s.split(/\s+/).filter((w) => w && !DROPPED_WORDS.has(w));
  if (kept.length === 0) return "";
  let joined = kept.join(" ");
  for (const [pattern, replacement] of SPELLING_VARIANTS) joined = joined.replace(pattern, replacement);
  const words = joined.split(" ");
  if (words.length === 1 && words[0] === "peppers") return "peppers";
  words[words.length - 1] = singularize(words[words.length - 1]);
  return words.join(" ");
}
